import type { ReactNode } from 'react'
import { Popover, PopoverContentWrapper, PopoverTriggerWrapper } from '@valiant/ui-shared'
import { useIsMobile } from '@valiant/ui-shared'

interface BasePopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: ReactNode
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  className?: string
  onMobileClick?: () => void
  isActive?: boolean
}

export function BasePopover({
  open,
  onOpenChange,
  trigger,
  children,
  align,
  side,
  sideOffset,
  className,
  onMobileClick,
  isActive,
}: BasePopoverProps) {
  const { isMobile } = useIsMobile()

  const defaultAlign = align || (isMobile ? 'center' : 'end')
  const defaultSide = side || (isMobile ? 'bottom' : 'right')
  const defaultSideOffset = sideOffset !== undefined ? sideOffset : isMobile ? 0 : 16

  // Handle mobile click to navigate instead of opening popover
  const handleTriggerClick = (e: React.MouseEvent) => {
    if (isMobile && onMobileClick) {
      e.preventDefault()
      e.stopPropagation()
      onMobileClick()
      return
    }
  }

  // On mobile with mobile click handler, we don't want the popover to open
  const shouldOpenPopover = !(isMobile && onMobileClick)

  return (
    <Popover open={shouldOpenPopover ? open : false} onOpenChange={onOpenChange}>
      <PopoverTriggerWrapper open={open} onClick={handleTriggerClick} active={isActive}>
        {trigger}
      </PopoverTriggerWrapper>

      <PopoverContentWrapper
        align={defaultAlign}
        side={defaultSide}
        sideOffset={defaultSideOffset}
        className={className}
      >
        {children}
      </PopoverContentWrapper>
    </Popover>
  )
}
